<template>
    <v-container class="p-relative">

        <!-- TOOLBAR -->
        <step-dots :step="step" @back="goBack"/>

        <!-- CAROUSEL -->
        <v-carousel :show-arrows="false" light hide-delimiters touchless v-model="carousel" height="100%"
                    class="overflow-visible">
            <!-- BASIC INFO -->
            <v-carousel-item>
                <basic-info @change="basicInfoData" stepper-mode/>
            </v-carousel-item>
            <!-- PROFESSIONAL INFO -->
            <v-carousel-item>
                <professional-info @change="professionalInfoData" stepper-mode/>
            </v-carousel-item>
            <!-- OBJECTIVES -->
            <v-carousel-item>
                <choose-objectives @change="objectives = $event" stepper-mode/>
            </v-carousel-item>
            <!-- PAYMENT METHOD -->
            <v-carousel-item>
                <template v-if="paymentSet">
                    <v-card color="transparent" flat>
                        <v-card-title>{{ $t('payment.payment_method') }}</v-card-title>
                        <v-card-text>
                            <payment-card :owner-id="currentUser && currentUser.uid || null"
                                          :payment-method="account.stripeAccount.paymentMethod"/>
                        </v-card-text>
                    </v-card>
                </template>
                <card-creator v-else ref="cardCreator" @success="onCreateSuccess" @cardData="onCardData"
                              :default-name="name" no-save-button stepper-mode/>
            </v-carousel-item>
            <!-- SELECT PLAN -->
            <v-carousel-item>
                <select-plan v-if="!showTerms" @change="planData" with-coupon stepper-mode @coupon="couponId = $event"/>
                <v-fade-transition>
                    <div v-show="showTerms" class="mt-5">

                        <v-alert outlined color="secondary" @click.native="downloadTerms" style="cursor: pointer"
                                 v-ripple>
                            <v-row class="px-3" align="center">
                                <b>{{ $t('entrance.terms_and_conditions') }}</b>
                                <v-spacer/>
                                <v-btn icon color="accent">
                                    <v-icon>mdi-download</v-icon>
                                </v-btn>
                            </v-row>
                        </v-alert>

                        <v-alert outlined color="accent" @click.native="downloadPolicy" style="cursor: pointer"
                                 v-ripple>
                            <v-row class="px-3" align="center">
                                <b>{{ $t('entrance.privacy_and_policy') }}</b>
                                <v-spacer/>
                                <v-btn icon color="secondary">
                                    <v-icon>mdi-download</v-icon>
                                </v-btn>
                            </v-row>
                        </v-alert>

                        <v-checkbox
                            v-model="acceptTerms"
                            :label="$t('entrance.accept_all')"
                            color="primary"
                            hide-details
                        ></v-checkbox>
                    </div>
                </v-fade-transition>
            </v-carousel-item>
        </v-carousel>

        <v-row class="container-bottom">
            <v-col cols="12" lg="6">
                <v-btn @click="continueStepper" color="primary" class="black--text normal-btn" depressed large block
                       :disabled="disabledContinue" :loading="loadingCard">
                    {{ carousel === 4 && showTerms ? $t('common.finish') : $t('common.continue') }}
                </v-btn>
            </v-col>
            <v-col cols="12" lg="6">
                <logout-button outlined color="black" large block/>
            </v-col>
        </v-row>

        <v-overlay z-index="50" :value="overlay">
            <v-progress-circular color="primary" indeterminate size="64"/>
        </v-overlay>

        <kimsa-confirm
            ref="confirmModal"
            :title="$t('common.notice')"
            :content="$t('coach.confirm_plan_stepper')"
            :loading="overlay"
            :persistent="false"
            @confirm="finishStepper"
            :close-on-confirm="false"
        />

    </v-container>
</template>

<script>

import StepDots from "@/components/entrance/elements/StepDots";
import BasicInfo from "@/components/company/BasicInfo";
import ProfessionalInfo from "@/components/company/ProfessionalInfo";
import ChooseObjectives from "@/components/company/ChooseObjectives";
import PaymentCard from "@/components/common/payment/PaymentCard";
import CardCreator from "@/components/common/payment/CardCreator";
import SelectPlan from "@/components/coach/SelectPlan";
import KimsaConfirm from "@/components/common/KimsaConfirm";

import firebase, {db} from '@/utils/Firebase'
import {mapActions} from 'vuex'
import LogoutButton from "@/components/common/inputs/LogoutButton";
import {loadStripe} from '@stripe/stripe-js';

export default {
    components: {
        LogoutButton,
        StepDots,
        BasicInfo,
        ProfessionalInfo,
        ChooseObjectives,
        PaymentCard,
        CardCreator,
        SelectPlan,
        KimsaConfirm,
    },
    data() {
        return {
            carousel: 0,
            overlay: false,
            valid: false,
            loadingCard: false,
            // Step 1
            name: '',
            country: '',
            phone: '',
            address: '',
            type: '',
            // Step 2
            description: '',
            experience: '',
            sport_areas: [],
            // Step 3
            objectives: [],
            // Step 4
            card: null,
            // Step 5
            plan: null,
            couponId: null,
            showTerms: false,
            acceptTerms: false,
        }
    },
    computed: {
        step(vm = this) {
            return vm.carousel + 1
        },
        disabledContinue(vm = this) {
            if (vm.carousel === 0 || vm.carousel === 3 || vm.carousel === 4) {
                if (vm.carousel === 4 && !vm.plan) return true
                if (vm.carousel === 4 && !vm.showTerms) return false
                if (vm.carousel === 4 && !vm.acceptTerms && vm.showTerms) return true
                if (vm.carousel === 3) {
                    if (vm.paymentSet) return false
                    return !vm.valid
                }
            }
            return false
        },
        paymentSet(vm = this) {
            return !!vm.account && !!vm.account.stripeAccount && !!vm.account.stripeAccount.paymentMethod
        },
    },
    methods: {
        basicInfoData(payload) {
            const vm = this
            vm.name = payload.name || ''
            vm.country = payload.country || ''
            vm.phone = payload.phone || ''
            vm.address = payload.address || ''
            vm.type = payload.type || ''
        },
        professionalInfoData(payload) {
            const vm = this
            vm.description = payload.description || ''
            vm.experience = payload.experience || ''
            vm.sport_areas = payload.sport_areas || ''
        },
        onCardData(payload) {
            const vm = this
            // console.log('onCardData payload', payload)
            vm.valid = !!(payload && payload.billing_details.name);
        },
        async onCreateSuccess({paymentMethod}) {
            const vm = this
            // console.log('onCreateSuccess', paymentMethod)

            const {card, billing_details, id} = paymentMethod

            let paymentPayload = {
                id: id,
                billing_details: {name: billing_details.name},
                card: {brand: card.brand, last4: card.last4}
            }

            await db.collection('users').doc(vm.currentUser.uid).update({
                name: vm.name,
                phone: vm.phone,
                'stripeAccount.paymentMethod': paymentPayload
            })
        },
        subscribePlanCoach: firebase.functions().httpsCallable('httpPayments-subscribePlanCoach'),
        planData(plan) {
            const vm = this
            vm.plan = plan
            vm.valid = plan !== null;
        },
        async continueStepper() {
            const vm = this
            if (vm.carousel === 0 && (!vm.name || (!vm.phone || vm.phone.length < 8))) return vm.toast(vm.$t('entrance.required_name_phone'), 'error')
            if (vm.carousel === 3 && !vm.paymentSet) {
                vm.loadingCard = true
                await vm.$refs.cardCreator.send()
                vm.loadingCard = false
                return vm.carousel++
            }
            if (vm.carousel !== 4) {
                vm.showTerms = false
                return vm.carousel++
            }
            if (vm.carousel === 4 && !vm.showTerms) return vm.showTerms = true
            if (vm.showTerms) await vm.showConfirmPlan()
        },
        showConfirmPlan() {
            const vm = this
            vm.$refs.confirmModal.show()
        },
        async finishStepper() {
            const vm = this
            let payload = {}
            vm.overlay = true

            // Step 1
            if (vm.name) payload.name = vm.name
            if (vm.country) payload.country = vm.country
            if (vm.phone) payload.phone = vm.phone
            if (vm.address) payload.address = vm.address
            if (vm.type) payload.type = vm.type
            // Step 2
            if (vm.description) payload.description = vm.description
            if (vm.experience) payload.experience = vm.experience
            if (vm.sport_areas.length > 0) payload.sport_areas = vm.sport_areas.map(sport => sport.value)
            // Step 3
            if (vm.objectives.length > 0) payload.objectives = JSON.parse(JSON.stringify(vm.objectives))
            // Step 4
            // Trigger cardCreator in carousel 3

            // console.log('payload', payload);

            await db.collection('users').doc(vm.currentUser.uid).update(payload)
            // Step 5
            await vm.setSubscriptionCoachPlan()
            vm.$emit('finish')
            vm.overlay = false
        },
        async setSubscriptionCoachPlan() {
            const vm = this
            let subscribePayload = {
                planId: vm.plan.priceId,
                isFree: false,
                addTrial: true,
                couponId: vm.couponId,
            }

            await vm.subscribePlanCoach(subscribePayload).then(async result => {
                if (result.data.requiresAction) {
                    const clientSecret = result.data.paymentIntentClientSecret
                    const stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY)
                    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret);
                    if (error) {
                        console.log('Error en la autenticación 3D Secure:', error.message);
                        vm.toast(`${vm.$t('payment.subscription_failed')}: 3DS`, 'error')
                    } else {
                        if (paymentIntent.status === 'succeeded') {
                            console.log('Pago completado con éxito');
                        }
                    }
                }
            }).catch(err => {
                console.log(`${vm.$t('payment.subscription_failed')}: ${err.message}`)
            })
        },
        goBack() {
            const vm = this
            if (vm.carousel === 0) return
            if (vm.carousel === 4 && vm.showTerms) return vm.showTerms = false
            return vm.carousel--
        },
        downloadTerms() {
            const link = document.createElement("a");
            link.href = 'docs/terms_and_conditions.pdf';
            link.setAttribute("download", `${this.$t('entrance.terms_and_conditions')} - BUILDER.pdf`);
            link.click();
        },
        downloadPolicy() {
            const link = document.createElement("a");
            link.href = 'docs/privacy_policy.pdf';
            link.setAttribute("download", `${this.$t('entrance.privacy_and_policy')} - BUILDER.pdf`);
            link.click();
        },
    },
    watch: {
        carousel(val) {
            if (val === 3 || val === 4) this.valid = false
            window.scrollTo(0, 0)
        },
    }
}
</script>

<style scoped>

.container-bottom {
    position: sticky;
    bottom: 0;
    margin-top: 4px;
    background-color: #FFFFFF;
}

::v-deep .v-carousel__item {
    overflow: visible !important
}

</style>